import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './IndustrySelector.css';

export interface IndustrySelectorProps {
  value: string[];
  onChange: (interests: string[]) => void;
  error?: string;
  showNextButton?: boolean;
  onNextQuestion?: () => void;
}

const POPULAR_INDUSTRIES = [
  'SaaS/Software',
  'E-commerce',
  'Digital Marketing',
  'Business Tools',
  'Mobile Apps',
  'Fintech',
  'Healthcare',
  'Online Education',
  'Content Creation',
  'Productivity Tools',
  'Data Analytics',
  'Social Media'
];

// Category keys for translation
const INDUSTRY_CATEGORY_KEYS = {
  'Business & Finance': 'categoryBusinessFinance',
  'Technology & Software': 'categoryTechSoftware',
  'Creative & Media': 'categoryCreativeMedia',
  'Health & Wellness': 'categoryHealthWellness',
  'Education & Learning': 'categoryEducationLearning',
  'Lifestyle & Consumer': 'categoryLifestyleConsumer',
  'Professional Services': 'categoryProfessionalServices',
  'Emerging & Innovation': 'categoryEmergingInnovation'
};

// Industry translation keys (only for industries that should be translated)
const INDUSTRY_TRANSLATION_KEYS: Record<string, string> = {
  // Business & Finance
  'E-commerce': 'industryEcommerce',
  'Fintech': 'industryFintech',
  'Business Tools': 'industryBusinessTools',
  'Productivity Tools': 'industryProductivityTools',
  'Project Management': 'industryProjectMgmt',
  'Accounting & Finance': 'industryAccounting',
  'Real Estate': 'industryRealEstate',
  'Insurance': 'industryInsurance',
  'Legal Services': 'industryLegal',
  'Consulting': 'industryConsulting',
  'Banking & Investment': 'industryBanking',
  'Business Intelligence': 'industryBusinessIntel',
  'CRM & Sales Tools': 'industryCRM',
  'HR & Recruiting': 'industryHR',
  // Technology & Software
  'SaaS/Software': 'industrySaaS',
  'Developer Tools': 'industryDevTools',
  'Cybersecurity': 'industryCybersecurity',
  'Data Analytics': 'industryDataAnalytics',
  'Artificial Intelligence': 'industryAI',
  'Machine Learning': 'industryML',
  'Blockchain/Crypto': 'industryBlockchain',
  'IoT/Hardware': 'industryIoT',
  'Cloud Services': 'industryCloud',
  'Mobile Apps': 'industryMobileApps',
  'Web Development': 'industryWebDev',
  'API & Integration': 'industryAPI',
  'DevOps Tools': 'industryDevOps',
  'No-Code/Low-Code': 'industryNoCode',
  // Creative & Media
  'Content Creation': 'industryContentCreation',
  'Digital Marketing': 'industryDigitalMarketing',
  'Social Media': 'industrySocialMedia',
  'Design & Graphics': 'industryDesign',
  'Photography': 'industryPhotography',
  'Video Production': 'industryVideo',
  'Gaming': 'industryGaming',
  'Entertainment': 'industryEntertainment',
  'Publishing': 'industryPublishing',
  'Advertising': 'industryAdvertising',
  'Influencer Marketing': 'industryInfluencer',
  'Audio & Podcasting': 'industryAudio',
  'Animation & 3D': 'industryAnimation',
  'Stock Media': 'industryStockMedia',
  // Health & Wellness
  'Healthcare': 'industryHealthcare',
  'Mental Health': 'industryMentalHealth',
  'Fitness & Sports': 'industryFitness',
  'Nutrition & Diet': 'industryNutrition',
  'Telemedicine': 'industryTelemedicine',
  'Medical Devices': 'industryMedicalDevices',
  'Wellness Apps': 'industryWellness',
  'Senior Care': 'industrySeniorCare',
  'Healthcare Analytics': 'industryHealthcareAnalytics',
  'Pharmacy & Medicine': 'industryPharmacy',
  // Education & Learning
  'Online Education': 'industryOnlineEdu',
  'Corporate Training': 'industryCorporateTraining',
  'Language Learning': 'industryLanguageLearning',
  'Skill Development': 'industrySkillDev',
  'Certification': 'industryCertification',
  'Tutoring': 'industryTutoring',
  'Educational Games': 'industryEduGames',
  'Research Tools': 'industryResearch',
  'Academic Software': 'industryAcademic',
  'Student Management': 'industryStudentMgmt',
  // Lifestyle & Consumer
  'Travel & Tourism': 'industryTravel',
  'Food & Beverage': 'industryFood',
  'Fashion & Beauty': 'industryFashion',
  'Home & Garden': 'industryHome',
  'Pets': 'industryPets',
  'Parenting': 'industryParenting',
  'Dating & Relationships': 'industryDating',
  'Hobbies & Crafts': 'industryHobbies',
  'Sports & Recreation': 'industrySports',
  'Personal Finance': 'industryPersonalFinance',
  // Professional Services
  'Customer Service': 'industryCustomerService',
  'Sales & CRM': 'industrySalesCRM',
  'Marketing Automation': 'industryMarketingAuto',
  'Event Management': 'industryEvents',
  'Logistics & Supply Chain': 'industryLogistics',
  'Manufacturing': 'industryManufacturing',
  'Agriculture': 'industryAgriculture',
  'Construction': 'industryConstruction',
  'Transportation': 'industryTransportation',
  'Energy & Utilities': 'industryEnergy',
  // Emerging & Innovation
  'Sustainability': 'industrySustainability',
  'Remote Work': 'industryRemoteWork',
  'Gig Economy': 'industryGigEconomy',
  'Community Building': 'industryCommunity',
  'Creator Economy': 'industryCreatorEconomy',
  'NFTs & Digital Assets': 'industryNFTs',
  'Virtual Reality': 'industryVR',
  'Automation': 'industryAutomation',
  'Space Technology': 'industrySpace',
  'Climate Tech': 'industryClimateTech'
};

const INDUSTRY_CATEGORIES = {
  'Business & Finance': [
    'E-commerce',
    'Fintech',
    'Business Tools',
    'Productivity Tools',
    'Project Management',
    'Accounting & Finance',
    'Real Estate',
    'Insurance',
    'Legal Services',
    'Consulting',
    'Banking & Investment',
    'Business Intelligence',
    'CRM & Sales Tools',
    'HR & Recruiting'
  ],
  'Technology & Software': [
    'SaaS/Software',
    'Developer Tools',
    'Cybersecurity',
    'Data Analytics',
    'Artificial Intelligence',
    'Machine Learning',
    'Blockchain/Crypto',
    'IoT/Hardware',
    'Cloud Services',
    'Mobile Apps',
    'Web Development',
    'API & Integration',
    'DevOps Tools',
    'No-Code/Low-Code'
  ],
  'Creative & Media': [
    'Content Creation',
    'Digital Marketing',
    'Social Media',
    'Design & Graphics',
    'Photography',
    'Video Production',
    'Gaming',
    'Entertainment',
    'Publishing',
    'Advertising',
    'Influencer Marketing',
    'Audio & Podcasting',
    'Animation & 3D',
    'Stock Media'
  ],
  'Health & Wellness': [
    'Healthcare',
    'Mental Health',
    'Fitness & Sports',
    'Nutrition & Diet',
    'Telemedicine',
    'Medical Devices',
    'Wellness Apps',
    'Senior Care',
    'Healthcare Analytics',
    'Pharmacy & Medicine'
  ],
  'Education & Learning': [
    'Online Education',
    'Corporate Training',
    'Language Learning',
    'Skill Development',
    'Certification',
    'Tutoring',
    'Educational Games',
    'Research Tools',
    'Academic Software',
    'Student Management'
  ],
  'Lifestyle & Consumer': [
    'Travel & Tourism',
    'Food & Beverage',
    'Fashion & Beauty',
    'Home & Garden',
    'Pets',
    'Parenting',
    'Dating & Relationships',
    'Hobbies & Crafts',
    'Sports & Recreation',
    'Personal Finance'
  ],
  'Professional Services': [
    'Customer Service',
    'Sales & CRM',
    'Marketing Automation',
    'Event Management',
    'Logistics & Supply Chain',
    'Manufacturing',
    'Agriculture',
    'Construction',
    'Transportation',
    'Energy & Utilities'
  ],
  'Emerging & Innovation': [
    'Sustainability',
    'Remote Work',
    'Gig Economy',
    'Community Building',
    'Creator Economy',
    'NFTs & Digital Assets',
    'Virtual Reality',
    'Automation',
    'Space Technology',
    'Climate Tech'
  ]
};

// Flatten all industries for easy searching
const AVAILABLE_INDUSTRIES = Object.values(INDUSTRY_CATEGORIES).flat();

export const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  value,
  onChange,
  error,
  showNextButton = false,
  onNextQuestion
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Popular');

  const filteredIndustries = searchTerm 
    ? AVAILABLE_INDUSTRIES.filter(industry =>
        industry.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : activeCategory === 'Popular'
    ? POPULAR_INDUSTRIES
    : activeCategory === 'All'
    ? AVAILABLE_INDUSTRIES
    : INDUSTRY_CATEGORIES[activeCategory as keyof typeof INDUSTRY_CATEGORIES] || POPULAR_INDUSTRIES;

  const handleIndustryToggle = (industry: string) => {
    if (value.includes(industry)) {
      onChange(value.filter(i => i !== industry));
    } else {
      onChange([...value, industry]);
    }
  };

  const handleRemoveIndustry = (industryToRemove: string) => {
    onChange(value.filter(industry => industry !== industryToRemove));
  };

  const handleSelectAll = () => {
    const categoryIndustries = activeCategory === 'Popular' 
      ? POPULAR_INDUSTRIES 
      : activeCategory === 'All'
      ? AVAILABLE_INDUSTRIES
      : INDUSTRY_CATEGORIES[activeCategory as keyof typeof INDUSTRY_CATEGORIES] || [];
    
    const newIndustries = [...new Set([...value, ...categoryIndustries])];
    onChange(newIndustries);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getIndustryCategory = (industry: string) => {
    if (POPULAR_INDUSTRIES.includes(industry)) return t('aiIdeaGenerator.popular');
    for (const [category, industries] of Object.entries(INDUSTRY_CATEGORIES)) {
      if (industries.includes(industry)) {
        return t(`aiIdeaGenerator.${INDUSTRY_CATEGORY_KEYS[category as keyof typeof INDUSTRY_CATEGORY_KEYS]}`);
      }
    }
    return 'Other';
  };

  const getTranslatedIndustryName = (industry: string) => {
    const translationKey = INDUSTRY_TRANSLATION_KEYS[industry];
    if (translationKey) {
      return t(`aiIdeaGenerator.${translationKey}`);
    }
    return industry; // Return original if no translation
  };

  return (
    <div className="industry-selector">
      {error && <div className="error-text" style={{marginBottom: '1rem'}}>{error}</div>}
      
      <div className="selector-layout">
        {/* Left Panel - Categories */}
        <div className="categories-panel">
          <h4>{t('aiIdeaGenerator.categories')}</h4>
          <div className="category-list">
            <div 
              className={`category-item ${activeCategory === 'Popular' ? 'active' : ''}`}
              onClick={() => setActiveCategory('Popular')}
            >
              <span>🔥 {t('aiIdeaGenerator.popular')}</span>
              <span className="category-count">{POPULAR_INDUSTRIES.length}</span>
            </div>
            <div 
              className={`category-item ${activeCategory === 'All' ? 'active' : ''}`}
              onClick={() => setActiveCategory('All')}
            >
              <span>🏢 {t('aiIdeaGenerator.allIndustries')}</span>
              <span className="category-count">{AVAILABLE_INDUSTRIES.length}</span>
            </div>
            {Object.entries(INDUSTRY_CATEGORIES).map(([category, industries]) => (
              <div 
                key={category}
                className={`category-item ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                <span>{t(`aiIdeaGenerator.${INDUSTRY_CATEGORY_KEYS[category as keyof typeof INDUSTRY_CATEGORY_KEYS]}`)}</span>
                <span className="category-count">{industries.length}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Middle Panel - Industries List */}
        <div className="items-panel">
          <div className="items-header">
            <div className="search-and-actions">
              <input
                type="text"
                placeholder={t('aiIdeaGenerator.searchIndustriesGlobally')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="global-search"
              />
              <button 
                onClick={handleSelectAll}
                className="select-all-btn"
                type="button"
              >
                {t('aiIdeaGenerator.selectAllIn')} {activeCategory}
              </button>
            </div>
          </div>
          
          <div className="items-summary">
            {t('aiIdeaGenerator.showing')} {filteredIndustries.length} {t('aiIdeaGenerator.industries')} • {value.length} {t('aiIdeaGenerator.selected')}
          </div>
          
          <div className="items-grid">
            {filteredIndustries.map(industry => (
              <div
                key={industry}
                className={`item-option ${value.includes(industry) ? 'selected' : ''}`}
                onClick={() => handleIndustryToggle(industry)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(industry)}
                  onChange={() => handleIndustryToggle(industry)}
                  className="item-checkbox"
                />
                <span className="item-name">{getTranslatedIndustryName(industry)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Selected Industries */}
        <div className="selected-panel">
          <div className="selected-header">
            <h4 className="selected-title">{t('aiIdeaGenerator.selectedIndustries')}</h4>
            <span className="selected-count">{value.length}</span>
            {value.length > 0 && (
              <button onClick={handleClearAll} className="clear-all-btn" type="button">
                {t('aiIdeaGenerator.clearAll')}
              </button>
            )}
          </div>
          
          <div className="selected-items-list">
            {value.length === 0 ? (
              <div className="empty-selection">
                <div className="empty-selection-icon">🏭</div>
                <div>{t('aiIdeaGenerator.selectFromList')}</div>
              </div>
            ) : (
              value.map(industry => (
                <div key={industry} className="selected-item">
                  <div>
                    <div className="selected-item-name">{getTranslatedIndustryName(industry)}</div>
                    <div className="selected-item-category">{getIndustryCategory(industry)}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveIndustry(industry)}
                    className="remove-item-btn"
                    type="button"
                    aria-label={`${t('aiIdeaGenerator.remove')} ${getTranslatedIndustryName(industry)}`}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
          
          {/* Next Question Button - Fixed at bottom */}
          {showNextButton && value.length > 0 && onNextQuestion && (
            <div className="next-question-container">
              <button
                onClick={onNextQuestion}
                className="next-question-btn-inline"
                type="button"
              >
                <span className="btn-icon">→</span>
                {t('aiIdeaGenerator.nextQuestion')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};